import {
  CoveragePriceInfo,
  PriceInfoElement,
} from '@qover-private/protos-web/qover/dojo/pricing/v1/pricing';
import { PaymentRythm } from '@qover/domain';
import { Model } from './Risk';
import type { Variant } from './Terms';

type Coverage = {
  monthlyPremium: number;
  yearlyPremium: number;
};

export class PriceInfo {
  priceInfo: Array<PriceInfoElement>;
  basePriceInfo: Array<PriceInfoElement>;
  initialized: boolean;

  constructor(
    initialized: boolean = false,
    priceInfo?: Array<PriceInfoElement>,
    basePriceInfo?: Array<PriceInfoElement>,
  ) {
    this.initialized = initialized;

    this.priceInfo = priceInfo ?? [];
    this.basePriceInfo = basePriceInfo ?? [];
  }

  private getPriceFromCoverage(coverage: CoveragePriceInfo): Coverage {
    return {
      monthlyPremium: coverage.monthlyPremium?.withTaxes ?? 0,
      yearlyPremium: coverage.yearlyPremium?.withTaxes ?? 0,
    };
  }

  getPriceInfoElement(
    arr: Array<PriceInfoElement>,
    model: Model,
    variant: Variant,
  ) {
    return arr
      .filter(
        el =>
          el.dimensions?.some(d => d.name === 'model' && d.value === model) &&
          el.dimensions?.some(d => d.name === 'variant' && d.value === variant),
      )
      .map(el => {
        const coverage = el.coverages?.filter(
          coverage => coverage.coverageName !== 'totalPrice',
        );
        return coverage?.[0] ?? {} as CoveragePriceInfo;
      })[0];
  }

  getPrice(
    model: Model,
    variant?: Variant,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    if (!variant) {
      return 0;
    }
    const priceInfo = this.getPriceInfoElement(this.priceInfo, model, variant);

    return paymentRythm === PaymentRythm.MONTHLY
      ? priceInfo?.monthlyPremium?.withTaxes ?? 0
      : priceInfo?.yearlyPremium?.withTaxes ?? 0;
  }

  getBasePrice(
    model: Model,
    variant?: Variant,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    if (!variant) {
      return 0;
    }

    const priceInfo = this.getPriceInfoElement(this.basePriceInfo, model, variant);

    return paymentRythm === PaymentRythm.MONTHLY
      ? priceInfo?.monthlyPremium?.withTaxes ?? 0
      : priceInfo?.yearlyPremium?.withTaxes ?? 0;
  }

  getDiscount(model: Model,variant?: Variant) {
    const discountPrice = this.getPrice(model, variant);
    const price = this.getBasePrice(model, variant);

    if (!price) {
      return 0;
    }

    const ratio = discountPrice / price;
    return Number((1 - ratio).toFixed(2)) * 100;
  }

  getDifferenceWithDiscount(
    model: Model,
    variant?: Variant,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    const discountPrice = this.getPrice(model, variant, paymentRythm);
    const price = this.getBasePrice(model, variant, paymentRythm);
    return discountPrice - price;
  }
}
